/**
 * Tenant Service
 * Handles tenant management API calls
 */

import api from './api';

const tenantService = {
  /**
   * Get all tenants (platform admin only)
   * @returns {Promise<Array>} List of tenants
   */
  getAllTenants: async () => {
    const response = await api.get('/v1/admin/tenants');
    return response.data.tenants || response.data;
  },

  /**
   * Get a specific tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Tenant details
   */
  getTenant: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}`);
    return response.data;
  },

  /**
   * Create a new tenant
   * @param {Object} tenantData - Tenant data
   * @returns {Promise<Object>} Created tenant
   */
  createTenant: async (tenantData) => {
    const response = await api.post('/v1/admin/tenants', tenantData);
    return response.data;
  },

  /**
   * Update a tenant
   * @param {string} tenantId - Tenant ID
   * @param {Object} updates - Update data
   * @returns {Promise<Object>} Updated tenant
   */
  updateTenant: async (tenantId, updates) => {
    const response = await api.put(`/v1/admin/tenants/${tenantId}`, updates);
    return response.data;
  },

  /**
   * Delete/deactivate a tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<void>}
   */
  deleteTenant: async (tenantId) => {
    await api.delete(`/v1/admin/tenants/${tenantId}`);
  },

  /**
   * Get tenant users
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} List of users
   */
  getTenantUsers: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/users`);
    return response.data.users || response.data;
  },

  /**
   * Get tenant usage statistics
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Usage stats
   */
  getTenantUsage: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/usage`);
    return response.data;
  },

  /**
   * Get tenant billing info
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Billing info
   */
  getTenantBilling: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/billing`);
    return response.data;
  },

  // =========================================================================
  // Admin API Key Management
  // =========================================================================

  /**
   * Get API key metadata for a tenant (admin only)
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} API key metadata (prefix, status, timestamps)
   */
  getApiKeyMetadata: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/api-key`);
    return response.data;
  },

  /**
   * Generate new API key for a tenant (admin only)
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Full API key (shown only once)
   */
  generateApiKey: async (tenantId) => {
    const response = await api.post(`/v1/admin/tenants/${tenantId}/api-key/generate`);
    return response.data;
  },

  /**
   * Revoke API key for a tenant (admin only)
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Revocation confirmation
   */
  revokeApiKey: async (tenantId) => {
    const response = await api.delete(`/v1/admin/tenants/${tenantId}/api-key`);
    return response.data;
  },

  // =========================================================================
  // Tenant Settings Management
  // =========================================================================

  /**
   * Get tenant settings (admin only)
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Tenant settings including system_config
   */
  getTenantSettings: async (tenantId) => {
    const response = await api.get(`/v1/admin/tenants/${tenantId}/settings`);
    return response.data;
  },

  /**
   * Update tenant settings (admin only)
   * @param {string} tenantId - Tenant ID
   * @param {Object} updates - Settings updates
   * @returns {Promise<Object>} Updated settings
   */
  updateTenantSettings: async (tenantId, updates) => {
    const response = await api.put(`/v1/admin/tenants/${tenantId}/settings`, updates);
    return response.data;
  },
};

export default tenantService;
